import React, { useState, useRef, useEffect } from 'react';
import { Users, Plus, Trash2, Bell, GripVertical, Check, X, AlertTriangle, ChevronDown, Search } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { teamAPI } from '../utils/api';
import DeleteButton from './ui/DeleteButton';

// Role assignment dropdown: default shows team-group members; "Search all members" lazy-loads everyone
const RoleSelect = ({
  value, onValueChange, groupName, groupMembers,
  allMembers, allMembersLoading, onLoadAllMembers, isInvalid, testId, team,
  blockedMemberIds,
}) => {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  // Focus the search input after it mounts (avoids autoFocus + Radix DismissableLayer conflict)
  useEffect(() => {
    if (showAll && open) {
      const t = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [showAll, open]);

  const close = () => { setOpen(false); setShowAll(false); setSearch(''); };

  const selectedMember =
    groupMembers.find(m => m.member_id === value) ||
    (allMembers || []).find(m => m.member_id === value) ||
    (team || []).find(m => m.member_id === value); // fallback: member assigned outside their default roles

  const handleSelect = (memberId) => {
    onValueChange(memberId === '_clear_' ? '' : memberId);
    close();
  };

  const handleShowAll = () => {
    setShowAll(true);
    onLoadAllMembers();
  };

  const filteredAll = (allMembers || []).filter(
    m => !search || m.name.toLowerCase().includes(search.toLowerCase())
  );

  const displayName = isInvalid
    ? 'Removed member — no longer available'
    : selectedMember?.name || null;

  return (
    <Popover open={open} onOpenChange={(o) => { if (!o) close(); else setOpen(true); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`w-full h-9 px-3 flex items-center justify-between text-sm border rounded-md transition-colors gap-2 ${
            isInvalid
              ? 'border-rose-400 ring-1 ring-rose-300 bg-rose-50'
              : 'border-slate-200 bg-white hover:bg-slate-50'
          }`}
        >
          <span className={`truncate flex-1 text-left ${
            isInvalid ? 'text-rose-600 font-medium'
            : displayName ? 'text-slate-900' : 'text-slate-400'
          }`}>
            {displayName || 'Select person'}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 w-64 shadow-lg border border-slate-200 rounded-lg overflow-hidden bg-white z-50"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {/* Search input — only in "show all" mode */}
        {showAll && (
          <div className="flex items-center border-b border-slate-200 px-3 bg-white">
            <Search className="w-4 h-4 text-slate-400 mr-2 flex-shrink-0" />
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              placeholder="Search all members..."
              className="flex-1 h-9 text-sm outline-none bg-transparent placeholder:text-slate-400"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600 ml-1">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        <div className="max-h-56 overflow-y-auto">
          {/* Clear / None */}
          <button
            type="button"
            onClick={() => handleSelect('_clear_')}
            className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 border-b border-slate-100"
          >
            — None —
          </button>

          {!showAll ? (
            <>
              {groupMembers.length > 0 ? (
                <>
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase text-slate-400 tracking-wider">
                    {groupName}
                  </div>
                  {groupMembers.map(m => {
                    const blocked = blockedMemberIds?.has(m.member_id);
                    return (
                    <button
                      key={m.member_id}
                      type="button"
                      onClick={() => { if (!blocked) handleSelect(m.member_id); }}
                      disabled={blocked}
                      title={blocked ? 'Unavailable on this service date' : undefined}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
                        blocked
                          ? 'opacity-40 cursor-not-allowed text-slate-400'
                          : value === m.member_id
                            ? 'bg-green-50 text-green-700 font-medium'
                            : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className={`truncate ${blocked ? 'line-through' : ''}`}>{m.name}</span>
                      {value === m.member_id && !blocked && <Check className="w-3.5 h-3.5 flex-shrink-0 ml-2" />}
                    </button>
                    );
                  })}
                </>
              ) : (
                <div className="px-3 py-3 text-xs text-slate-400">No members in {groupName}</div>
              )}

              {/* Search all members button */}
              <div className="border-t border-slate-100 mt-1">
                <button
                  type="button"
                  onClick={handleShowAll}
                  className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 flex items-center gap-2"
                >
                  {allMembersLoading
                    ? <span className="w-3.5 h-3.5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                    : <Search className="w-3.5 h-3.5" />
                  }
                  Search all members...
                </button>
              </div>
            </>
          ) : (
            /* Show all mode */
            allMembersLoading ? (
              <div className="flex justify-center py-6">
                <span className="w-5 h-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase text-slate-400 tracking-wider">
                  {search ? `Results for "${search}"` : 'All members'}
                </div>
                {filteredAll.length > 0 ? filteredAll.map(m => {
                    const blocked = blockedMemberIds?.has(m.member_id);
                    return (
                  <button
                    key={m.member_id}
                    type="button"
                    onClick={() => { if (!blocked) handleSelect(m.member_id); }}
                    disabled={blocked}
                    title={blocked ? 'Unavailable on this service date' : undefined}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
                      blocked
                        ? 'opacity-40 cursor-not-allowed text-slate-400'
                        : value === m.member_id
                          ? 'bg-green-50 text-green-700 font-medium'
                          : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`truncate ${blocked ? 'line-through' : ''}`}>{m.name}</span>
                    {value === m.member_id && !blocked && <Check className="w-3.5 h-3.5 flex-shrink-0 ml-2" />}
                  </button>
                    );
                }) : (
                  <div className="px-3 py-4 text-sm text-slate-400 text-center">
                    {search ? 'No members found' : 'No members available'}
                  </div>
                )}
              </>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Read-only display: fieldset per team, grouped by member (no duplicate names), matches Program viewer style
const StatusBadge = ({ status }) => (
  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
    status === 'accepted' ? 'bg-emerald-50 text-emerald-700'
    : status === 'declined' ? 'bg-rose-50 text-rose-700'
    : 'bg-amber-50 text-amber-600'
  }`}>
    {status === 'accepted' ? 'Accepted' : status === 'declined' ? 'Declined' : 'Pending'}
  </span>
);

const groupByMember = (assignments) => {
  const order = [];
  const map = {};
  for (const a of assignments) {
    if (!map[a.member_id]) {
      map[a.member_id] = [];
      order.push(a.member_id);
    }
    map[a.member_id].push(a);
  }
  return order.map(id => ({ member_id: id, assignments: map[id] }));
};

const PeopleViewerMode = ({ formData, team, isPastService }) => {
  const teamsWithAssignments = (formData.team_assignments || [])
    .map(entry => ({
      ...entry,
      assignments: (entry.assignments || []).filter(a => a.member_id),
    }))
    .filter(entry => entry.assignments.length > 0);

  if (teamsWithAssignments.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="people-viewer-empty">
        <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">No team assignments yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="people-viewer-content">
      <div className="space-y-5">
        {teamsWithAssignments.map(entry => {
          const memberGroups = groupByMember(entry.assignments);
          return (
            <fieldset key={entry.group} className="border border-slate-300 rounded-lg p-0 overflow-hidden" data-testid={`viewer-team-${entry.group}`}>
              <legend className="ml-4 px-2 text-sm font-semibold text-slate-700">{entry.group}</legend>
              <ol className="list-none px-5 pb-4 pt-1 space-y-3">
                {memberGroups.map((group) => {
                  const member = team.find(m => m.member_id === group.member_id);
                  const memberDeleted = !isPastService && !member;
                  return (
                    <li
                      key={group.member_id}
                      className={`flex items-start rounded-md p-2 -mx-2 ${memberDeleted ? 'bg-rose-50 ring-1 ring-rose-200' : ''}`}
                      data-testid={`viewer-member-${entry.group}-${group.member_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {memberDeleted && <AlertTriangle className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />}
                            <p className={`text-sm font-medium truncate ${memberDeleted ? 'text-rose-700' : 'text-slate-900'}`}>
                              {member?.name || 'Removed member'}
                            </p>
                          </div>
                          <StatusBadge status={group.assignments[0]?.status} />
                        </div>
                        <div className="mt-1 space-y-0.5">
                          {group.assignments.map(a => (
                            <p key={a.role} className="text-xs text-slate-500" data-testid={`viewer-role-row-${entry.group}-${a.role}`}>
                              {a.role}
                            </p>
                          ))}
                        </div>
                        {memberDeleted && (
                          <p className="text-[11px] text-rose-500 mt-1.5">
                            This member has been removed. Please reassign.
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
};

const PeopleTab = ({
  formData, setFormData, hasEditPermission,
  groups, roles, team, notifying, handleNotifyAll, onGroupAdded, isPastService, deletedMemberIds,
  blockedMemberIds,
}) => {
  const [isAddTeamOpen, setIsAddTeamOpen] = useState(false);
  const [addTeamGroupId, setAddTeamGroupId] = useState(null);
  // Shared all-members list — lazily loaded when any row's "Search all members" is clicked
  const [allMembers, setAllMembers] = useState(null);
  const [allMembersLoading, setAllMembersLoading] = useState(false);

  const loadAllMembers = async () => {
    if (allMembers !== null || allMembersLoading) return;
    setAllMembersLoading(true);
    try {
      const res = await teamAPI.getAll({ limit: 9999 });
      setAllMembers(res.data.members || []);
    } catch { setAllMembers([]); }
    finally { setAllMembersLoading(false); }
  };

  const getAvailableGroups = () => {
    const added = (formData.team_assignments || []).map(t => t.group);
    return groups.filter(g => !added.includes(g.name));
  };

  const addTeamGroup = () => {
    const group = groups.find(g => g.group_id === addTeamGroupId);
    if (!group) return;
    const groupRoles = roles.filter(r => r.group === group.name);
    const entry = {
      group: group.name,
      assignments: groupRoles.map(r => ({ role: r.name, member_id: '' })),
    };
    setFormData(prev => ({ ...prev, team_assignments: [...(prev.team_assignments || []), entry] }));
    if (onGroupAdded) onGroupAdded(group.name);
    setAddTeamGroupId(null);
    setIsAddTeamOpen(false);
  };

  const removeTeamGroup = (groupIdx) => {
    setFormData(prev => ({
      ...prev,
      team_assignments: prev.team_assignments.filter((_, i) => i !== groupIdx),
    }));
  };

  const updateTeamAssignment = (groupIdx, roleIdx, memberId) => {
    setFormData(prev => {
      // Status is per-member per-service. If the new person already has another
      // role in this service, carry their existing service status across.
      // Otherwise the new assignment starts as pending (no status field).
      let existingStatus;
      prev.team_assignments.forEach((t, gi) => {
        t.assignments.forEach((a, ri) => {
          if (gi === groupIdx && ri === roleIdx) return; // skip the slot being changed
          if (a.member_id === memberId && a.status) existingStatus = a.status;
        });
      });

      return {
        ...prev,
        team_assignments: prev.team_assignments.map((t, gi) => {
          if (gi !== groupIdx) return t;
          return {
            ...t,
            assignments: t.assignments.map((a, ri) => {
              if (ri !== roleIdx) return a;
              if (memberId !== a.member_id) {
                return {
                  role: a.role,
                  member_id: memberId,
                  ...(existingStatus ? { status: existingStatus } : {}),
                };
              }
              return a;
            }),
          };
        }),
      };
    });
  };

  const updateAssignmentStatus = (memberId, newStatus) => {
    setFormData(prev => ({
      ...prev,
      team_assignments: prev.team_assignments.map(t => ({
        ...t,
        assignments: t.assignments.map(a =>
          a.member_id === memberId ? { ...a, status: newStatus } : a
        ),
      })),
    }));
  };

  const handleTeamDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    setFormData(prev => {
      const next = Array.from(prev.team_assignments);
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      return { ...prev, team_assignments: next };
    });
  };

  if (!hasEditPermission) {
    return <PeopleViewerMode formData={formData} team={team} isPastService={isPastService} />;
  }

  const availableGroups = getAvailableGroups();

  return (
    <>
      <div data-testid="people-tab-wrapper">
        <div className="flex gap-2 justify-end mb-4">
          {(formData.team_assignments || []).length > 0 && (
            <Button variant="outline" size="sm" data-testid="notify-all-btn" className="rounded-full gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleNotifyAll} disabled={notifying}>
              {notifying
                ? <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <Bell className="w-4 h-4" />
              }
              <span className="hidden sm:inline">{notifying ? 'Sending...' : 'Notify Assignment Change'}</span>
            </Button>
          )}
          <Button size="sm" data-testid="add-team-btn" className="rounded-full bg-green-700 hover:bg-green-800 gap-2"
            onClick={() => { setAddTeamGroupId(null); setIsAddTeamOpen(true); }}
            disabled={availableGroups.length === 0}>
            <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Team</span>
          </Button>
        </div>

        {(!formData.team_assignments || formData.team_assignments.length === 0) ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="people-tab-empty">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No teams added yet. Add a team to assign people.</p>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleTeamDragEnd}>
            <Droppable droppableId="teams-list" type="TEAM" isCombineEnabled={false} ignoreContainerClipping={false}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4" data-testid="people-tab-content">
                  {formData.team_assignments.map((teamEntry, gIdx) => (
                    <Draggable key={teamEntry.group} draggableId={`team-${teamEntry.group}`} index={gIdx}>
                      {(drag, snap) => (
                        <div ref={drag.innerRef} {...drag.draggableProps}
                          className={`rounded-xl border overflow-hidden transition-shadow bg-white ${snap.isDragging ? 'shadow-lg border-green-300' : 'shadow-sm border-slate-200'}`}
                          data-testid={`team-group-${gIdx}`}>
                          <div className="flex items-center gap-2 bg-slate-800 text-white px-4 py-3" {...drag.dragHandleProps}>
                            <GripVertical className="w-4 h-4 text-slate-400 cursor-grab active:cursor-grabbing" />
                            <Users className="w-4 h-4 text-slate-400" />
                            <span className="flex-1 font-semibold text-sm">{teamEntry.group}</span>
                            <DeleteButton
                              onConfirm={() => removeTeamGroup(gIdx)}
                              className="p-1"
                              title="Remove team group"
                              testId={`remove-team-${gIdx}`}
                            />
                          </div>
                          <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
                            {(teamEntry.assignments || []).map((assignment, rIdx) => {
                              // Show only members who carry this specific role
                              const roleMembers = team.filter(
                                m => (m.roles || []).includes(assignment.role)
                              );
                              // Invalid only if member is completely deleted from the system
                              const isInvalid = !isPastService && !!assignment.member_id
                                && (deletedMemberIds || new Set()).has(assignment.member_id);
                                return (
                                  <div
                                    key={assignment.role}
                                    className={`flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 px-4 py-2.5 ${isInvalid ? 'bg-rose-50' : ''}`}
                                    data-testid={`role-row-${gIdx}-${rIdx}`}
                                  >
                                    <div className={`flex items-center gap-1 sm:w-40 sm:flex-shrink-0 ${isInvalid ? 'text-rose-600' : 'text-slate-500 sm:text-slate-600'}`}>
                                      {isInvalid && <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />}
                                      <span className="text-xs sm:text-sm font-medium sm:font-normal truncate">{assignment.role}</span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <div className="flex-1 min-w-0">
                                        <RoleSelect
                                          value={assignment.member_id || ''}
                                          onValueChange={(v) => updateTeamAssignment(gIdx, rIdx, v)}
                                          groupName={assignment.role}
                                          groupMembers={roleMembers}
                                          allMembers={allMembers}
                                          allMembersLoading={allMembersLoading}
                                          onLoadAllMembers={loadAllMembers}
                                          isInvalid={isInvalid}
                                          testId={`person-select-${gIdx}-${rIdx}`}
                                          team={team}
                                          blockedMemberIds={blockedMemberIds}
                                        />
                                      </div>
                                    {assignment.member_id && (
                                      <div className="inline-flex rounded-md overflow-hidden border border-slate-200 flex-shrink-0" data-testid={`status-toggle-${gIdx}-${rIdx}`}>
                                        <button onClick={() => updateAssignmentStatus(assignment.member_id, assignment.status === 'accepted' ? 'pending' : 'accepted')}
                                          className={`flex items-center px-2.5 py-1.5 text-xs font-medium transition-colors ${
                                            assignment.status === 'accepted' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-400 hover:bg-emerald-50 hover:text-emerald-600'
                                          }`} data-testid={`accept-toggle-${gIdx}-${rIdx}`}>
                                          <Check className="w-3 h-3" />
                                        </button>
                                        <button onClick={() => updateAssignmentStatus(assignment.member_id, assignment.status === 'declined' ? 'pending' : 'declined')}
                                          className={`flex items-center px-2.5 py-1.5 text-xs font-medium transition-colors border-l border-slate-200 ${
                                            assignment.status === 'declined' ? 'bg-rose-600 text-white' : 'bg-white text-slate-400 hover:bg-rose-50 hover:text-rose-600'
                                          }`} data-testid={`decline-toggle-${gIdx}-${rIdx}`}>
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <Dialog open={isAddTeamOpen} onOpenChange={setIsAddTeamOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Add Team</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Team *</Label>
              <Select value={addTeamGroupId || undefined} onValueChange={(val) => setAddTeamGroupId(val)}>
                <SelectTrigger data-testid="team-group-select" className="mt-1"><SelectValue placeholder="Select a team" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {availableGroups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addTeamGroup} data-testid="submit-add-team-btn" className="w-full" disabled={!addTeamGroupId}>Add Team</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PeopleTab;
